/*
 * Authors: mariushirjanu
 * Created: 21/12/2022
 */
import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { GaMessageService } from '../../../shared/message/ga-message.service';
import { ICartItemStatus, ICatalogCartItem, IPunchoutCartItem, PriceCartService } from '../cart/price-cart.service';
import { GA1Service, IGA1IncomingCartItem, IGA1OutgoingCartItem } from '../../../shared/ga1/ga1.service';
import { GaDeferredService, IGADeferred } from '../../../shared/services/ga-deferred.service';
import { ICatalogCategory } from './order-catalog-data.service';
import { GaEventService } from '../../../shared/services/ga-event.service';
import { EVENT_ORDER_CATEGORIES_LOADED, EVENT_ORDER_CURRENCY_CHANGED, EVENT_ORDER_EXT_CATEGORY_CHANGED } from '../../../shared/app-events';
import { FormattingService } from '../../../shared/services/formatting.service';
import { CatalogCategoryType } from './overview/catalog-overview.service';
import { MaxWidth } from '../../../shared/responsive/responsive.service';

const IMG_URL_PREFIX:string = '/images';

export interface ICatalogLayout {
  name:string;
  title:string;
  icon:string;
  width?:MaxWidth;
  productsPerPage: {
    options:number[];
    current:number;
  }
}

export interface ICatalogProductImageList {
  tiny:string[];
  small:string[];
  medium:string[];
  large:string[];
}

@Injectable()
export class OrderCatalogService {
  private _currency:string;
  private _billType:string;
  private _workflowType:string;
  private _webshopId:number;
  private _countryId:number;
  private _companyCountryId:number;
  private _showStock:boolean;
  private _layout:string|ICatalogLayout;
  private _sort:string;
  private _scrollPosition:number;
  private _scrollTarget:string;
  private _autoSwitchLayout:boolean = true;
  private _started:IGADeferred<boolean> = this.deferredService.defer();
  private _categoriesDeferred:IGADeferred<ICatalogCategory[]> = this.deferredService.defer();
  private _singleExternalCategory:boolean;
  private _punchoutCatalog:boolean;
  private _showProposeAlternatives:boolean;
  private _categories:ICatalogCategory[];
  private _filteredCategories:ICatalogCategory[];
  private _accessTypeButtonCategory:ICatalogCategory[];
  private _isOnlyExternal:boolean;
  private _hidePrices:boolean;
  private _isLeasing:boolean;
  private _showOnlyInStockItems:boolean;
  private _stockOrderType:string;

  constructor(
    private deferredService:GaDeferredService,
    private formattingService:FormattingService,
    private messageService:GaMessageService,
    private priceCartService:PriceCartService,
    private ga1Service:GA1Service,
    private eventService:GaEventService
  ) {
    /***/
  }

  public start():void {
    this.recreateStartedPromisesIfNeeded();
    this._started.resolve(true);
  }

  public isStarted():Promise<boolean> {
    this.recreateStartedPromisesIfNeeded();
    return this._started.promise;
  }

  public get currency():string {
    return this._currency;
  }

  public set currency(currency:string) {
    this._currency = currency;
    this.eventService.sendMessage(EVENT_ORDER_CURRENCY_CHANGED, currency);
  }

  public get workflowType():string {
    return this._workflowType;
  }

  public set workflowType(workflowType:string) {
    this._workflowType = workflowType;
  }

  public get billType():string {
    return this._billType;
  }

  public set billType(billType:string) {
    this._billType = billType;
  }

  public get webshopId():number {
    return this._webshopId;
  }

  public set webshopId(webshopId:number) {
    this._webshopId = webshopId;
  }

  public get countryId():number {
    return this._countryId;
  }

  public set countryId(countryId:number) {
    this._countryId = countryId;
  }

  public get companyCountryId():number {
    return this._companyCountryId;
  }

  public set companyCountryId(countryId:number) {
    this._companyCountryId = countryId;
  }

  public get showStock():boolean {
    return this._showStock;
  }

  public set showStock(showStock:boolean) {
    this._showStock = showStock;
  }

  public get layout():string | ICatalogLayout {
    return this._layout;
  }

  public set layout(layout:string | ICatalogLayout) {
    this._layout = layout;
  }

  public get sort():string {
    return this._sort;
  }

  public set sort(sort:string) {
    this._sort = sort;
  }


  public get stockOrderType():string {
    return this._stockOrderType;
  }

  public set stockOrderType(value:string) {
    this._stockOrderType = value;
  }

  public get singleExternalCategory():boolean {
    return this._singleExternalCategory;
  }

  public set singleExternalCategory(isSingle:boolean) {
    this._singleExternalCategory = isSingle;
    this.eventService.sendMessage(EVENT_ORDER_EXT_CATEGORY_CHANGED, isSingle);
  }

  public get punchoutCatalog():boolean {
    return this._punchoutCatalog;
  }

  public set punchoutCatalog(isPunchoutCatalog:boolean) {
    this._punchoutCatalog = isPunchoutCatalog;
  }

  public get showProposeAlternatives():boolean {
    return this._showProposeAlternatives;
  }

  public set showProposeAlternatives(showProposeAlternatives:boolean) {
    this._showProposeAlternatives = showProposeAlternatives;
  }

  public get scrollTarget():string {
    return this._scrollTarget;
  }

  public set scrollTarget(scrollTarget:string) {
    this._scrollTarget = scrollTarget;
  }

  public get scrollPosition():number {
    return this._scrollPosition;
  }

  public set scrollPosition(scrollPosition:number) {
    this._scrollPosition = scrollPosition;
  }

  public updateCartHandler = (product:ICatalogCartItem | IPunchoutCartItem, count:number):Promise<string> => {
    const deferred:IGADeferred<any> = this.deferredService.defer();

    if (product) {
      const mapped:IGA1OutgoingCartItem[] = [
        {
          id: product.id,
          count,
          product: {
            productId: product.id,
            part: product.part,
            title: product.title,
            shortDescription: product.shortDescription,
            price: product.price || {
              amount: (<IPunchoutCartItem>product).extUnitPrice,
              currency: (<IPunchoutCartItem>product).extCurrency
            },
            unitLeasePrice: product.unitLeasePrice,
            leasePeriod: product.leasePeriod,
            leasePriceTotal: product.unitLeasePriceTotal,
            partSuffix: (<ICatalogCartItem>product).partSuffix,
            partSuffixDescription: (<ICatalogCartItem>product).partSuffixDescription,
            leadTime: (<ICatalogCartItem>product).leadTime,
            category: (<IPunchoutCartItem>product).category,
            oppositePriceConversion: (<IPunchoutCartItem>product).oppositePriceConversion,
            vendorId: (<IPunchoutCartItem>product).vendorId,
            catalogId: (<IPunchoutCartItem>product).catalogId
          }
        }
      ];

      this.ga1Service.addToCart(mapped)
        .then((message:IGA1IncomingCartItem) => {
          //replace the cartstatus with what's returned from ga1
          this.updateCart(undefined, message[ 1 ]);
          deferred.resolve();
        })
        .catch((rejection:boolean) => {
          this.messageService.showErrorMessage(`Product has not been added to the cart: ${rejection}`);
          this._started.reject(rejection);
          deferred.reject(rejection);
        });
    } else {
      deferred.reject();
    }

    return deferred.promise;
  };

  public imageURL(image, type:string):string {
    return `${IMG_URL_PREFIX}/${type}/${image.url}`;
  }

  //returns an object containing 4 lists of urls (Strings): tiny, small, medium, large
  public imageURLs(images):ICatalogProductImageList {
    const iURLs:ICatalogProductImageList = {
      tiny: [],
      small: [],
      medium: [],
      large: []
    };

    _.forEach(images, (image) => {
      _.forEach(iURLs, (urls:any, type:string) => {
        urls.push(this.imageURL(image, type));
      });
    });

    return iURLs;
  }

  private createCartItemStatus(cartItem:IGA1IncomingCartItem):ICartItemStatus {
    return {
      count: cartItem.count,
      cartCount: cartItem.count,
      countCartItem: undefined,
      id: cartItem.id || cartItem.orderLineNumber,
      itemId: cartItem.itemId || cartItem.id || cartItem.orderLineNumber,
      product: {
        id: cartItem.id || cartItem.orderLineNumber,
        itemId: cartItem.itemId || cartItem.id || cartItem.orderLineNumber,
        productId: cartItem.id || cartItem.orderLineNumber,
        orderLineNumber: cartItem.orderLineNumber,
        price: undefined,
        title: undefined,
        shortDescription: undefined,
        description: undefined,
        part: undefined,
        partConfigurationExist: undefined
      }
    };
  }

  public updateCart = (event:Event, cartData:IGA1IncomingCartItem[]):void => {
    this.priceCartService.cartStatus = _.chain(cartData)
      .filter((cartItem:IGA1IncomingCartItem) => !!cartItem.count)
      .map((cartItem:IGA1IncomingCartItem) => this.createCartItemStatus(cartItem))
      .value();
  };

  public get autoSwitchLayout():boolean {
    return this._autoSwitchLayout;
  }

  public set autoSwitchLayout(autoSwitchLayout:boolean) {
    this._autoSwitchLayout = autoSwitchLayout;
  }

  private recreateStartedPromisesIfNeeded():void {
    if (!this._started) {
      this._started = this.deferredService.defer();
    }
  }

  public set categories(catalogCategories:ICatalogCategory[]){
    this._categories = catalogCategories;
    const accessTypeButtonFilters:any = { accessType: 'BUTTON' };
    this._filteredCategories = _.reject(catalogCategories, accessTypeButtonFilters);
    this._accessTypeButtonCategory = _.filter(catalogCategories, accessTypeButtonFilters);
    this._isOnlyExternal = catalogCategories?.length === _.filter(catalogCategories, (category:ICatalogCategory) => category.type === CatalogCategoryType.EXTERNAL).length;
    this.eventService.sendMessage(EVENT_ORDER_CATEGORIES_LOADED, catalogCategories);
    this._categoriesDeferred.resolve(catalogCategories);
  }

  public get categories():ICatalogCategory[] {
    return this._categories;
  }

  public startLoadingCategories():void {
    this._categoriesDeferred = this.deferredService.defer();
  }

  public get categoriesPromise():Promise<ICatalogCategory[]> {
    return this._categoriesDeferred.promise;
  }

  public get filteredCategories():ICatalogCategory[] {
    return this._filteredCategories;
  }

  public get accessTypeButtonCategory():ICatalogCategory[] {
    return this._accessTypeButtonCategory;
  }

  public get isOnlyExternal():boolean {
    return this._isOnlyExternal;
  }

  public get hidePrices():boolean {
    return this._hidePrices;
  }

  public set hidePrices(hidePrices:boolean) {
    this._hidePrices = hidePrices;
  }

  public get isLeasingWebshop():boolean {
    return this._isLeasing;
  }

  public set isLeasingWebshop(isLeasing:boolean) {
    this._isLeasing = isLeasing;
  }

  public get showOnlyInStockItems():boolean {
    return this._showOnlyInStockItems;
  }

  public set showOnlyInStockItems(showOnlyInStockItems:boolean) {
    this._showOnlyInStockItems = showOnlyInStockItems;
  }

  public formatAmount = (price:number, count?:number, currency?:string) => {
    return this.formattingService.formatAmount(price * (count || 1), currency || this.priceCartService.currency);
  };

  public clear():void {
    for (const key in this) {
      if (key.startsWith('_')) {
        this[ key ] = undefined;
      }
    }
  }
}